<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import { useLoadingView } from '@/features/miiaFw/composables/useLoadingView'
import { useOverheadImage } from '../../composables/overheadImage/useOverheadImage'
import { useOverheadImageStore, type ImageUrl } from '@/stores/overheadImageUrlsStore'
import { useZoomOverheadImage } from '@/composables/overheadImage/useZoomOverheadImage'
import ZoomOverheadImage from './ZoomOverheadImage.vue'
import IconButton from '../menu/IconButton.vue'

const { setLoadingView } = useLoadingView()
const overheadImage = useOverheadImage()
const overheadImageStore = useOverheadImageStore()
const zoomOverheadImage = useZoomOverheadImage()

const currentPage = ref(1)
const perPage = ref(12)
const maxModalHeight = ref(0)

const imageListModalRef = ref<HTMLDivElement | null>(null)

/**
 * 画像クリック
 * */
const handleImageClick = async (imageUrl: ImageUrl): Promise<void> => {
  setLoadingView(true)
  zoomOverheadImage.close()
  await nextTick()
  await zoomOverheadImage.open(imageUrl)
}

/**
 * モーダルの最大の高さを設定する
 **/
const changeMaxModalHeight = (): void => {
  if (imageListModalRef.value) {
    const height = imageListModalRef.value.offsetHeight
    if (maxModalHeight.value < height) maxModalHeight.value = height
  }
}

/**
 * モーダルを閉じる
 **/
const closeModal = (): void => {
  zoomOverheadImage.close()
  currentPage.value = 1
  maxModalHeight.value = 0

  overheadImageStore.removeImageUrls()
  overheadImage.close()
}

/**
 * ページに表示するスクリーニング画像S3URLリストを取得する
 * @return ページに表示するスクリーニング画像S3URLリスト
 **/
const paginatedImageUrls = computed<ImageUrl[]>(() => {
  const start = perPage.value * (currentPage.value - 1)
  const end = perPage.value * currentPage.value
  return overheadImageStore.imageUrls.slice(start, end)
})

/**
 * スクリーニング画像S3URLの数を取得する
 * @return スクリーニング画像S3URLリストの数
 **/
const imageUrlsCount = computed(() => overheadImageStore.imageUrls.length)
</script>

<template>
  <div v-if="overheadImage.isOpen.value">
    <div
      class="image-list-modal"
      ref="imageListModal"
      :style="[{ 'min-height': maxModalHeight + 'px' }]"
    >
      <IconButton :icon="['fas', 'fa-square-xmark']" @click="closeModal"></IconButton>
      <div class="image-list-wrapper">
        <div class="image-list">
          <div
            v-for="(imageUrl, index) of paginatedImageUrls"
            @click="handleImageClick(imageUrl)"
            :key="index"
            class="list-image-wrapper"
          >
            <img class="list-image" :src="imageUrl.thumbnail" />
          </div>
        </div>
        <b-pagination
          v-model="currentPage"
          :total-rows="imageUrlsCount"
          :per-page="perPage"
          :align="'center'"
          size="lg"
          @change="changeMaxModalHeight"
        />
      </div>
      <ZoomOverheadImage class="zoom-image-modal-wrapper"></ZoomOverheadImage>
    </div>
  </div>
</template>
<style scoped>
.image-list-modal {
  width: 100%;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  border: 1px solid #ccc;
  padding: 10px;
}
.image-list-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-top: 10px;
}
.image-list {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  padding-bottom: 10px;
}
.list-image-wrapper {
  width: 24%;
  margin: 0.5%;
  cursor: pointer;
}
.list-image {
  width: 100%;
  max-height: 20vh;
  object-fit: contain;
}
.zoom-image-modal-wrapper {
  position: fixed;
  left: 0;
  top: 0;
  z-index: calc(infinity);
  background-color: rgba(0, 0, 0, 0.5);
  height: 100vh;
  width: 100%;
  display: flex;
  justify-content: center;
}
</style>
